import { useLayoutEffect, useRef } from "react";
import { Link, Outlet } from "react-router-dom";
import { ArrowRight, Users } from "lucide-react";
import gsap from "gsap";
import { cn } from "@/lib/utils";
import logo from "@/assets/habits365greek-logo.png";

/**
 * Route layout for /client/login and /client/signup.
 *
 * Stays mounted across login↔signup navigation so:
 *   1. The right marketing column doesn't re-mount / re-animate on every
 *      route swap — it's static chrome, not part of the form transition.
 *   2. The page-level scrollbar doesn't flicker during the swap because
 *      the form area is its own scroll container (`overflow-y-auto`), not
 *      the whole document.
 *
 * Inner pages render their own heading + subtitle + form + footer so they
 * can each animate independently when they mount. The layout itself only
 * animates the chrome once on first mount.
 *
 * Visual chrome (solid portal pill, bordered cross-link pill, GSAP fade
 * stagger) mirrors the InternalAuthShell so the two surfaces feel like
 * the same product. Split-screen + light palette is kept — that's the
 * client portal's distinguishing style.
 */
export const AuthShell = () => {
  const rootRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const targets = root.querySelectorAll<HTMLElement>("[data-fade]");
    if (!targets.length) return;

    if (reduceMotion) {
      gsap.set(targets, { opacity: 1, y: 0 });
      return;
    }

    const ctx = gsap.context(() => {
      gsap.set(targets, { opacity: 0, y: 10 });
      gsap.to(targets, {
        opacity: 1,
        y: 0,
        duration: 0.35,
        ease: "power2.out",
        stagger: 0.05,
      });
    }, root);

    return () => ctx.revert();
  }, []);

  return (
    <div ref={rootRef} className="h-screen bg-background flex overflow-hidden">
      {/* Left: form column */}
      <div className="flex-1 flex flex-col min-w-0">
        <div
          data-fade
          className="px-4 py-5 sm:px-6 lg:px-8 flex items-center justify-between gap-3 shrink-0"
        >
          <Link to="/client/login" className="flex items-center gap-2.5 group">
            <img
              src={logo}
              alt="Habits 365 Greek"
              className="h-8 object-contain dark:invert dark:brightness-200"
            />
            <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-primary text-primary-foreground text-xs font-semibold uppercase tracking-wide shadow-sm transition-transform group-hover:scale-105">
              Client Portal
            </span>
          </Link>
          <Link
            to="/auth"
            className="group inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border bg-card/80 backdrop-blur text-xs font-medium text-foreground hover:bg-card hover:border-primary/40 hover:shadow-sm transition-all"
          >
            <Users className="h-3.5 w-3.5 text-primary" />
            Org team
            <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </div>

        {/* Form area scrolls independently — keeps page-level scrollbar away
            during route transitions and on short viewports. `min-h-full` on
            the flex inner container is what enables BOTH vertical centering
            (when content is short) AND natural scrolling (when content is
            tall): the container grows past min-h-full as needed. */}
        <div className="flex-1 overflow-y-auto">
          <div className="min-h-full flex items-center justify-center px-4 py-8 sm:px-6 lg:px-8">
            <div className="w-full max-w-md">
              <Outlet />
            </div>
          </div>
        </div>
      </div>

      {/* Right: marketing column (lg+ only). Static across login↔signup. */}
      <div
        data-fade
        className="hidden lg:flex flex-1 relative bg-gradient-to-br from-accent via-muted to-background"
      >
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="max-w-md text-center space-y-5">
            <span className="inline-flex items-center px-3 py-1 rounded-full bg-background/80 backdrop-blur text-xs font-semibold uppercase tracking-wide text-primary">
              Welcome to your portal
            </span>
            <h2 className="text-2xl font-semibold leading-8 tracking-tight">
              Order custom Greek-life apparel without the back-and-forth.
            </h2>
            <p className="text-sm leading-6 text-muted-foreground">
              Browse the catalog, request proofs, approve quotes, place orders — and track every step of the way. All in one place.
            </p>

            <div className="grid grid-cols-3 gap-3 pt-4">
              <FeaturePill label="Browse Catalog" className="bg-info-background text-info" />
              <FeaturePill label="Request Proofs" className="bg-warning-background text-warning" />
              <FeaturePill label="Track Orders" className="bg-success-background text-success" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const FeaturePill = ({ label, className }: { label: string; className?: string }) => (
  <div className={cn("rounded-lg px-3 py-2 text-xs font-medium", className)}>{label}</div>
);

export default AuthShell;
